/**
 * CSV Field Guide (issue #780)
 *
 * Per-entity-type column reference + downloadable template generation for
 * the Bulk Operations admin page. Grounded in the actual backend parsing
 * path — NOT aspirational:
 *
 * - `id` and `name` are the only columns enforced at the API boundary:
 *   - Every CSV row must have a non-empty `id` column, for EVERY operation
 *     type (backend/src/routes/bulk-operations.ts `parseCsv` caller rejects
 *     the whole request with 400 VALIDATION_ERROR otherwise).
 *   - `name` is required only for IMPORT/VALIDATE (backend/src/services/
 *     BulkOperationService.ts `handleImport`); UPDATE/DELETE don't check it.
 *   - All other "required" columns below reflect the entity's public
 *     GraphQL contract (non-null fields in schema.graphql), not enforcement
 *     by the bulk-operations endpoint — the endpoint will happily write an
 *     entity missing those fields, producing an incomplete node that later
 *     GraphQL reads may render as blank/null. Fill them in.
 * - Only the `tags` column is auto-split into an array (comma-separated).
 *   Every other array-valued field (e.g. `memberIds`, `stakeholderIds`,
 *   `primaryKeys`) is written as the literal CSV string — it will NOT
 *   become an array via CSV. Use the JSON items textarea for those fields.
 * - CSV header names are restricted to `[A-Za-z0-9_.\- ]` (see `SAFE_HEADER`
 *   in bulk-operations.ts) — no brackets, colons, or other JSON-ish
 *   punctuation in a column name.
 * - The CSV file-upload control in this page is only shown for IMPORT and
 *   VALIDATE operations; UPDATE (needs a nested `changes` object/array per
 *   row) and DELETE (needs a boolean `cascade` column) don't validate
 *   against the flat-object CSV shape and must use the JSON items textarea
 *   instead.
 */

export interface CsvColumnGuide {
  /** CSV header name — must match backend SAFE_HEADER charset. */
  column: string;
  /** Non-null per the entity's GraphQL contract — see file header caveat. */
  required: boolean;
  /** Human-readable format/type description. */
  format: string;
  /** Additional guidance (enum values, edge/index side-effects, caveats). */
  notes?: string;
  /** Example value used when generating a downloadable CSV template. */
  example: string;
}

export interface EntityCsvGuide {
  entityType: string;
  /**
   * False for entity types that appear in the Entity Type dropdown but have
   * no GraphQL type/repository yet (#481/#482) — TechnologyComponent,
   * ChangeInitiative, Roadmap. Field names for these are unverified.
   */
  hasFullSchema: boolean;
  /** One-line description of the entity, shown above its column table. */
  summary: string;
  /** Entity-specific columns, in addition to the universal id/name columns. */
  columns: CsvColumnGuide[];
}

/** Columns required/meaningful for every entity type's CSV, regardless of type. */
export const UNIVERSAL_COLUMNS: CsvColumnGuide[] = [
  {
    column: 'id',
    required: true,
    format: 'string',
    notes:
      'Mandatory on every CSV row for every operation. A CSV with any row missing id is ' +
      'rejected entirely (400 VALIDATION_ERROR) before any entity-specific validation runs.',
    example: 'svc-example-001',
  },
  {
    column: 'name',
    required: true,
    format: 'string',
    notes:
      'Required for IMPORT/VALIDATE (the two operations that support CSV upload in this page). ' +
      'A missing/blank name fails that row with EMPTY_NAME.',
    example: 'Example Entity',
  },
];

const KNOWN_ENTITY_GUIDES_MAP: Record<string, EntityCsvGuide> = {
  Service: {
    entityType: 'Service',
    hasFullSchema: true,
    summary: 'An application/integration/platform/infrastructure service in the catalog.',
    columns: [
      {
        column: 'shortName',
        required: true,
        format: 'string',
        example: 'AUTH',
      },
      {
        column: 'serviceType',
        required: true,
        format: 'enum',
        notes: 'One of: Application | Integration | Platform | Infrastructure | Website',
        example: 'Application',
      },
      {
        column: 'lifecycleStatus',
        required: true,
        format: 'enum',
        notes: 'One of: prototype | development | production | deprecated | retired',
        example: 'production',
      },
      {
        column: 'tier',
        required: true,
        format: 'enum',
        notes: 'One of: tier0 | tier1 | tier2 | tier3 (criticality)',
        example: 'tier1',
      },
      {
        column: 'teamId',
        required: false,
        format: 'string (entity id)',
        notes:
          'Owning team id. Populates the Service→Team ownership index (GSI3) so the service ' +
          'appears under that team; omit for an unowned service.',
        example: 'team-platform-001',
      },
      {
        column: 'tags',
        required: false,
        format: 'array — comma-separated',
        notes: 'The only column auto-split into an array. Quote the cell if any tag has a comma.',
        example: 'security,okta',
      },
      {
        column: 'description',
        required: false,
        format: 'string',
        example: 'Handles user authentication.',
      },
    ],
  },
  Team: {
    entityType: 'Team',
    hasFullSchema: true,
    summary: 'An organizational team that owns services.',
    columns: [
      {
        column: 'type',
        required: true,
        format: 'enum',
        notes: 'One of: development | operations | platform | security | data',
        example: 'platform',
      },
      {
        column: 'leadUserId',
        required: true,
        format: 'string',
        example: 'user-jdoe',
      },
      {
        column: 'contactEmail',
        required: true,
        format: 'string (email)',
        example: 'team-platform@example.com',
      },
      {
        column: 'memberIds',
        required: false,
        format: 'array — NOT auto-split',
        notes:
          'Non-null array field, but CSV only auto-splits the tags column. A memberIds cell ' +
          'is written as one literal string, not an array. Use the JSON items textarea instead.',
        example: '',
      },
      {
        column: 'parentTeamId',
        required: false,
        format: 'string (entity id)',
        example: '',
      },
    ],
  },
  BusinessCapability: {
    entityType: 'BusinessCapability',
    hasFullSchema: true,
    summary: 'A business capability node in the capability map hierarchy.',
    columns: [
      {
        column: 'level',
        required: true,
        format: 'integer',
        example: '1',
      },
      {
        column: 'criticality',
        required: true,
        format: 'enum',
        notes: 'One of: critical | important | supporting | nice_to_have',
        example: 'important',
      },
      {
        column: 'strategicImportance',
        required: true,
        format: 'enum',
        notes: 'One of: strategic | core | supporting',
        example: 'core',
      },
      {
        column: 'lifecycleStatus',
        required: true,
        format: 'enum',
        notes: 'One of: planned | developing | active | mature | deprecated | retired',
        example: 'active',
      },
      {
        column: 'businessOwnerId',
        required: true,
        format: 'string',
        example: 'user-jdoe',
      },
      {
        column: 'owningBusinessUnitId',
        required: true,
        format: 'string (entity id)',
        example: 'bu-finance',
      },
      {
        column: 'parentCapabilityId',
        required: false,
        format: 'string (entity id)',
        example: '',
      },
      {
        column: 'tags',
        required: false,
        format: 'array — comma-separated',
        example: 'core',
      },
      {
        column: 'stakeholderIds',
        required: false,
        format: 'array — NOT auto-split',
        notes: 'Same caveat as Team.memberIds — use the JSON items textarea for this field.',
        example: '',
      },
    ],
  },
  BusinessUnit: {
    entityType: 'BusinessUnit',
    hasFullSchema: true,
    summary: 'An organizational business unit.',
    columns: [
      {
        column: 'unitType',
        required: true,
        format: 'enum',
        notes: 'One of: division | group | team | partner',
        example: 'division',
      },
      {
        column: 'lifecycleStatus',
        required: true,
        format: 'enum',
        notes: 'One of: active | inactive | merged | disbanded',
        example: 'active',
      },
      {
        column: 'parentBusinessUnitId',
        required: false,
        format: 'string (entity id)',
        example: '',
      },
      {
        column: 'code',
        required: false,
        format: 'string',
        example: 'FIN',
      },
      {
        column: 'tags',
        required: false,
        format: 'array — comma-separated',
        example: '',
      },
    ],
  },
  Technology: {
    entityType: 'Technology',
    hasFullSchema: true,
    summary: 'A technology product/version tracked for lifecycle and licensing.',
    columns: [
      {
        column: 'lifecycleStage',
        required: true,
        format: 'enum',
        notes: 'One of: Adopt | Hold | Evaluate | Sunset',
        example: 'Adopt',
      },
      {
        column: 'version',
        required: false,
        format: 'string',
        example: '3.2.1',
      },
      {
        column: 'supplierId',
        required: false,
        format: 'string (entity id)',
        example: '',
      },
      {
        column: 'supportEndDate',
        required: false,
        format: 'date — ISO 8601 (e.g. 2027-12-31)',
        example: '2027-12-31',
      },
      {
        column: 'tags',
        required: false,
        format: 'array — comma-separated',
        example: '',
      },
    ],
  },
  DataDomain: {
    entityType: 'DataDomain',
    hasFullSchema: true,
    summary: 'A domain grouping related data objects for governance.',
    columns: [
      {
        column: 'domainType',
        required: true,
        format: 'enum',
        notes: 'One of: master | reference | transactional | analytical | metadata',
        example: 'master',
      },
      {
        column: 'dataStewardId',
        required: true,
        format: 'string',
        example: 'user-jdoe',
      },
      {
        column: 'owningBusinessUnitId',
        required: true,
        format: 'string (entity id)',
        example: 'bu-finance',
      },
      {
        column: 'defaultClassification',
        required: true,
        format: 'enum',
        notes: 'One of: public | internal | confidential | restricted',
        example: 'internal',
      },
      {
        column: 'lifecycleStatus',
        required: true,
        format: 'enum',
        notes: 'One of: planned | developing | active | mature | deprecated | retired',
        example: 'active',
      },
      {
        column: 'criticalityLevel',
        required: true,
        format: 'enum',
        notes: 'One of: critical | high | medium | low',
        example: 'high',
      },
      {
        column: 'containsPII',
        required: true,
        format: 'boolean — literal "true" or "false"',
        notes: 'Any other value (e.g. "yes", "1") is written as a string, not a real boolean.',
        example: 'false',
      },
      {
        column: 'containsPCI',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'containsPHI',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'tags',
        required: false,
        format: 'array — comma-separated',
        example: '',
      },
    ],
  },
  DataObject: {
    entityType: 'DataObject',
    hasFullSchema: true,
    summary: 'A specific data asset within a data domain.',
    columns: [
      {
        column: 'domainId',
        required: true,
        format: 'string (entity id)',
        example: 'domain-customer',
      },
      {
        column: 'objectType',
        required: true,
        format: 'enum',
        notes: 'One of: entity | event | aggregate | value_object | reference | fact | dimension',
        example: 'entity',
      },
      {
        column: 'classification',
        required: true,
        format: 'enum',
        notes: 'One of: public | internal | confidential | restricted',
        example: 'internal',
      },
      {
        column: 'lifecycleStatus',
        required: true,
        format: 'enum',
        notes: 'One of: planned | developing | active | mature | deprecated | retired',
        example: 'active',
      },
      {
        column: 'containsPII',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'containsPCI',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'containsPHI',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'encryptionRequired',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'maskingRequired',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'isGoldenRecord',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'false',
      },
      {
        column: 'rightsToDelete',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'true',
      },
      {
        column: 'rightsToAccess',
        required: true,
        format: 'boolean — literal "true" or "false"',
        example: 'true',
      },
      {
        column: 'tags',
        required: false,
        format: 'array — comma-separated',
        example: '',
      },
    ],
  },
};

// A Map (rather than computed bracket access on the object above) so entityType
// lookups can't trip the object-injection lint rule — Map#get is not a sink.
const KNOWN_ENTITY_GUIDES = new Map<string, EntityCsvGuide>(
  Object.entries(KNOWN_ENTITY_GUIDES_MAP)
);

/** Entity types shown in the dropdown with no GraphQL type/repository yet (#481/#482). */
const UNVERIFIED_ENTITY_TYPES = new Set(['TechnologyComponent', 'ChangeInitiative', 'Roadmap']);

function buildUnverifiedGuide(entityType: string): EntityCsvGuide {
  return {
    entityType,
    hasFullSchema: false,
    summary:
      `${entityType} has no GraphQL type/repository yet (tracked in #481/#482) — field names ` +
      'below are unverified. Only id/name are confirmed; check the backend entity definition ' +
      'before importing other columns.',
    columns: [],
  };
}

function buildGenericGuide(entityType: string): EntityCsvGuide {
  return {
    entityType,
    hasFullSchema: false,
    summary:
      `No curated column guide exists yet for "${entityType}". Only id/name are enforced by ` +
      'the bulk-operations endpoint — consult the matching type in ' +
      'backend/src/graphql/schema.graphql for its full field list before importing.',
    columns: [],
  };
}

/** Look up the CSV field guide for an entity type, with a safe fallback for unknown types. */
export function getEntityCsvGuide(entityType: string): EntityCsvGuide {
  const known = KNOWN_ENTITY_GUIDES.get(entityType);
  if (known) return { ...known, columns: [...known.columns] };
  if (UNVERIFIED_ENTITY_TYPES.has(entityType)) return buildUnverifiedGuide(entityType);
  return buildGenericGuide(entityType);
}

/** All entity types with a curated (schema-verified) CSV field guide. */
export function getKnownEntityTypesWithGuide(): string[] {
  return [...KNOWN_ENTITY_GUIDES.keys()];
}

/** Escape a single CSV cell per the RFC 4180 quoting rules the backend parser expects. */
export function csvEscapeCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Build a downloadable CSV template for an entity type: a header row followed
 * by one example data row, using the universal id/name columns plus every
 * entity-specific column in the guide.
 */
export function buildCsvTemplate(entityType: string): string {
  const guide = getEntityCsvGuide(entityType);
  const columns = [...UNIVERSAL_COLUMNS, ...guide.columns];
  const header = columns.map((c) => csvEscapeCell(c.column)).join(',');
  const exampleRow = columns.map((c) => csvEscapeCell(c.example)).join(',');
  return `${header}\n${exampleRow}\n`;
}
